import React from 'react'
import { Badge } from '~/components/Badge'
import { Carousel } from '~/components/Carousel'
import { Checkmark, Stars } from '~/components/Icons'
import { Image } from '~/components/Photos'
import type { ReviewsSection as Settings } from '~/types'
import { BREAKPOINTS, classnames, useWindowSize } from '~/utils'
import { attributesToProps, Section, Title } from './Common'

interface Props {
	section: Settings
}

export const ReviewsSection: React.FC<Props> = ({ section }) => {
	const screen = useWindowSize()

	return (
		<Section
			tag={section.tag}
			full
			className={classnames('space-y-12 text-center', {
				'pt-24 lg:!pt-28': section.settings.top_padding,
				'pb-24 lg:!pb-28': section.settings.bottom_padding
			})}
			style={{ backgroundColor: section.settings.background_color, color: section.settings.color }}
		>
			<header className="space-y-6 px-6 text-center">
				{section.settings.badge && <Badge>{section.settings.badge}</Badge>}
				{section.settings.title && <Title>{section.settings.title}</Title>}
				{section.settings.description && (
					<p dangerouslySetInnerHTML={{ __html: section.settings.description }} />
				)}
			</header>
			<Carousel
				className="space-x-8 pb-20 pt-4"
				snap={screen.width <= BREAKPOINTS.xs ? 'snap-center' : 'snap-start'}
				scrollMargin="scroll-m-4"
			>
				{section.blocks.map(({ id, settings, attributes }, i) => (
					<div
						{...attributesToProps(attributes)}
						id={id}
						key={i}
						className={classnames(
							'h-full rounded-2xl bg-white p-10 drop-shadow-xl md:p-12',
							'flex flex-col items-center justify-between space-y-3 md:space-y-4',
							{ 'ml-12': i === 0, 'mr-12': i === section.blocks.length - 1 },
							{ 'w-100 md:w-112': !section.settings.block_width }
						)}
						style={{ width: section.settings.block_width || undefined }}
					>
						<Image
							className="block aspect-square w-full rounded-3xl object-cover"
							src={settings.image}
							dimensions="responsive"
						/>
						{section.settings.show_score && <Stars rating={settings.rating} className="!mt-7" />}
						{settings.title && <h3 className="text-3xl font-bold">{settings.title}</h3>}
						{(settings.body.toString().length > 5 || settings.author) && (
							<p className="flex-auto text-2xl" dangerouslySetInnerHTML={{ __html: settings.body }} />
						)}
						{settings.author && (
							<p className="text-14 space-x-2">
								<span>{settings.author}</span>
								<span className="text-e4/8 inline-block rounded-full bg-cyan-500 px-1 py-1.5 align-text-bottom leading-none text-white">
									<Checkmark className="inline align-middle" />
								</span>
							</p>
						)}
					</div>
				))}
			</Carousel>
		</Section>
	)
}
